import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

export default function MinpakuRegistrationPage() {
  const { t, i18n } = useTranslation();

  useEffect(() => {
    window.scrollTo(0, 0);

    const title = t('seo.minpakuRegistration.title');
    const description = t('seo.minpakuRegistration.description');

    document.title = title;

    const setMeta = (selector: string, attr: string, value: string) => {
      const el = document.querySelector(selector);
      if (el) el.setAttribute(attr, value);
    };

    setMeta('meta[name="description"]', 'content', description);
    setMeta('meta[name="keywords"]', 'content', t('seo.minpakuRegistration.keywords'));
    setMeta('meta[property="og:title"]', 'content', title);
    setMeta('meta[property="og:description"]', 'content', description);
    setMeta('meta[property="og:url"]', 'content', `${SITE_URL}/minpaku-registration-tokyo`);
    setMeta('link[rel="canonical"]', 'href', `${SITE_URL}/minpaku-registration-tokyo`);
    setMeta('meta[name="twitter:title"]', 'content', title);
    setMeta('meta[name="twitter:description"]', 'content', description);
    setMeta('meta[name="last-modified"]', 'content', '2025-07-14');

    const schemaId = 'schema-minpaku-registration';
    let script = document.getElementById(schemaId) as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement('script');
      script.id = schemaId;
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'HowTo',
      name: title,
      description: description,
      url: `${SITE_URL}/minpaku-registration-tokyo`,
      totalTime: 'P3M',
      supply: [
        { '@type': 'HowToSupply', name: 'Building registry (登記簿謄本)' },
        { '@type': 'HowToSupply', name: 'Floor plans (平面図)' },
        { '@type': 'HowToSupply', name: 'Fire safety certificate' },
      ],
      step: [
        { '@type': 'HowToStep', name: 'Initial Consultation & Assessment', position: 1 },
        { '@type': 'HowToStep', name: 'Document Preparation', position: 2 },
        { '@type': 'HowToStep', name: 'Facility Compliance', position: 3 },
        { '@type': 'HowToStep', name: 'Application Submission', position: 4 },
        { '@type': 'HowToStep', name: 'Inspection & Review', position: 5 },
        { '@type': 'HowToStep', name: 'License Issuance & Launch', position: 6 },
      ],
      author: {
        '@type': 'Organization',
        name: 'Magome Kominka',
        url: `${SITE_URL}/`,
      },
    });

    return () => {
      const el = document.getElementById(schemaId);
      if (el) el.remove();
    };
  }, [i18n.language, t]);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-b from-[#F5F1E8] to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl md:text-5xl font-bold text-[#8B4513] mb-6 text-center">
            Step-by-Step Minpaku Registration in Tokyo
          </h1>
          <p className="text-xl text-gray-600 text-center max-w-3xl mx-auto">
            A comprehensive guide to navigating Tokyo's short-term rental licensing process, based on real operational experience in Ota City
          </p>
        </div>
      </section>

      {/* Main Content */}
      <article className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Process Overview */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-[#8B4513] mb-6 flex items-center">
              <div className="w-10 h-10 flex items-center justify-center mr-4">
                <i className="ri-map-pin-line text-3xl text-[#B8860B]"></i>
              </div>
              Process Overview
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                Launching a <strong>Tokyo minpaku</strong> or short-term rental business requires navigating Japan's complex regulatory framework. In Tokyo, property owners must choose between two primary licensing paths: the <strong>Minpaku (住宅宿泊事業法)</strong> registration under the Housing Accommodation Business Act, or the more comprehensive <strong>Ryokan (旅館業法)</strong> license under the Hotel Business Act.
              </p>
              <p>
                The Minpaku registration allows operation for up to 180 days per year and involves notification to local authorities rather than full licensing. This path is suitable for property owners seeking supplementary income with limited operational commitment. However, the 180-day restriction significantly impacts revenue potential, making it less attractive for serious <strong>Airbnb management Tokyo</strong> operations.
              </p>
              <p>
                The Ryokan license, particularly the Simple Lodging (簡易宿所) category, permits year-round operation without day restrictions. This is the preferred path for professional <strong>Tokyo Airbnb support</strong> businesses and property owners committed to maximizing rental income. Our team operates under a Ryokan license in Ota City, providing us with firsthand experience in both the application process and ongoing compliance requirements.
              </p>
              <p>
                The registration process typically involves five major phases: preliminary consultation and property assessment, document preparation and facility compliance verification, formal application submission, inspection by local authorities, and final approval with business commencement. Each phase presents unique challenges that can delay or derail applications without proper guidance and preparation.
              </p>
            </div>
          </section>

          {/* Required Documents */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-[#8B4513] mb-6 flex items-center">
              <div className="w-10 h-10 flex items-center justify-center mr-4">
                <i className="ri-file-list-3-line text-3xl text-[#B8860B]"></i>
              </div>
              Required Documents
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4 mb-8">
              <p>
                Document preparation represents one of the most time-consuming aspects of <strong>minpaku registration Tokyo</strong>. The specific requirements vary by ward and licensing type, but certain core documents are universally required. Missing or incorrectly prepared documents are the primary cause of application delays and rejections.
              </p>
            </div>

            <div className="bg-[#F5F1E8] rounded-lg p-8 space-y-6">
              <div className="border-l-4 border-[#B8860B] pl-6">
                <h3 className="text-xl font-bold text-[#8B4513] mb-3">Property Documentation</h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-[#B8860B] mt-1 mr-3"></i>
                    <span><strong>Building registry (登記簿謄本)</strong> - Must be current within 3 months and show clear ownership or authorization</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-[#B8860B] mt-1 mr-3"></i>
                    <span><strong>Floor plans (平面図)</strong> - Detailed architectural drawings showing room dimensions, emergency exits, and facility locations</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-[#B8860B] mt-1 mr-3"></i>
                    <span><strong>Fire safety equipment layout</strong> - Placement of fire extinguishers, smoke detectors, and emergency lighting</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-[#B8860B] mt-1 mr-3"></i>
                    <span><strong>Building confirmation certificate (検査済証)</strong> - Proof of building code compliance at construction</span>
                  </li>
                </ul>
              </div>

              <div className="border-l-4 border-[#B8860B] pl-6">
                <h3 className="text-xl font-bold text-[#8B4513] mb-3">Operator Documentation</h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-[#B8860B] mt-1 mr-3"></i>
                    <span><strong>Business registration (法人登記簿謄本)</strong> - For corporate applicants, current within 3 months</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-[#B8860B] mt-1 mr-3"></i>
                    <span><strong>Resident certificate (住民票)</strong> - For individual applicants, showing current address</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-[#B8860B] mt-1 mr-3"></i>
                    <span><strong>Management agreement</strong> - If using third-party <strong>Airbnb management Tokyo</strong> services</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-[#B8860B] mt-1 mr-3"></i>
                    <span><strong>Cleaning and maintenance contracts</strong> - Documented arrangements for property upkeep</span>
                  </li>
                </ul>
              </div>

              <div className="border-l-4 border-[#B8860B] pl-6">
                <h3 className="text-xl font-bold text-[#8B4513] mb-3">Compliance Documentation</h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-[#B8860B] mt-1 mr-3"></i>
                    <span><strong>Fire safety inspection certificate</strong> - Issued by qualified fire safety inspector</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-[#B8860B] mt-1 mr-3"></i>
                    <span><strong>Building use consent</strong> - From building management or owners' association if applicable</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-[#B8860B] mt-1 mr-3"></i>
                    <span><strong>Neighborhood notification records</strong> - Documentation of informing nearby residents</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-[#B8860B] mt-1 mr-3"></i>
                    <span><strong>Emergency response plan</strong> - Detailed procedures for guest emergencies and complaints</span>
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* Timeline */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-[#8B4513] mb-6 flex items-center">
              <div className="w-10 h-10 flex items-center justify-center mr-4">
                <i className="ri-time-line text-3xl text-[#B8860B]"></i>
              </div>
              Timeline
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4 mb-8">
              <p>
                Understanding the realistic timeline for <strong>ryokan license application Tokyo</strong> is crucial for business planning. Many applicants underestimate the time required, leading to financial strain and missed market opportunities. Based on our direct experience providing <strong>minpaku license support</strong> in Tokyo, here is a realistic timeline breakdown.
              </p>
            </div>

            <div className="space-y-6">
              <div className="bg-white border-2 border-[#B8860B]/20 rounded-lg p-6 hover:border-[#B8860B] transition-all duration-300">
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-16 h-16 bg-[#B8860B] text-white rounded-full flex items-center justify-center font-bold text-xl mr-6">
                    1-2
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-[#8B4513] mb-2">Weeks 1-2: Initial Consultation &amp; Assessment</h3>
                    <p className="text-gray-700">
                      Property evaluation, licensing path determination, preliminary document review, and feasibility assessment. This phase identifies potential obstacles before significant investment.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white border-2 border-[#B8860B]/20 rounded-lg p-6 hover:border-[#B8860B] transition-all duration-300">
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-16 h-16 bg-[#B8860B] text-white rounded-full flex items-center justify-center font-bold text-xl mr-6">
                    3-6
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-[#8B4513] mb-2">Weeks 3-6: Document Preparation</h3>
                    <p className="text-gray-700">
                      Gathering property documents, obtaining building certifications, preparing floor plans, securing fire safety inspections, and drafting operational procedures. This is typically the longest phase.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white border-2 border-[#B8860B]/20 rounded-lg p-6 hover:border-[#B8860B] transition-all duration-300">
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-16 h-16 bg-[#B8860B] text-white rounded-full flex items-center justify-center font-bold text-xl mr-6">
                    7-8
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-[#8B4513] mb-2">Weeks 7-8: Facility Compliance</h3>
                    <p className="text-gray-700">
                      Installing required safety equipment, making necessary property modifications, ensuring accessibility compliance, and preparing for official inspection.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white border-2 border-[#B8860B]/20 rounded-lg p-6 hover:border-[#B8860B] transition-all duration-300">
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-16 h-16 bg-[#B8860B] text-white rounded-full flex items-center justify-center font-bold text-xl mr-6">
                    9
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-[#8B4513] mb-2">Week 9: Application Submission</h3>
                    <p className="text-gray-700">
                      Formal submission to ward office, payment of application fees, scheduling of inspection date, and initial administrative review.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white border-2 border-[#B8860B]/20 rounded-lg p-6 hover:border-[#B8860B] transition-all duration-300">
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-16 h-16 bg-[#B8860B] text-white rounded-full flex items-center justify-center font-bold text-xl mr-6">
                    10-12
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-[#8B4513] mb-2">Weeks 10-12: Inspection &amp; Review</h3>
                    <p className="text-gray-700">
                      On-site inspection by health and fire safety officials, document verification, addressing any deficiencies identified, and awaiting final administrative approval.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white border-2 border-[#B8860B]/20 rounded-lg p-6 hover:border-[#B8860B] transition-all duration-300">
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-16 h-16 bg-[#B8860B] text-white rounded-full flex items-center justify-center font-bold text-xl mr-6">
                    13+
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-[#8B4513] mb-2">Week 13+: License Issuance &amp; Launch</h3>
                    <p className="text-gray-700">
                      Receiving official license, registering with tourism platforms, launching marketing, and commencing operations. Professional <strong>Tokyo Airbnb support</strong> ensures smooth launch execution.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-8 bg-[#FFF9E6] border-l-4 border-[#B8860B] p-6 rounded-r-lg">
              <p className="text-gray-700">
                <strong>Total Timeline:</strong> 3-4 months for straightforward applications with experienced guidance. First-time applicants without professional support often require 6-8 months or longer due to document resubmissions and compliance corrections.
              </p>
            </div>
          </section>

          {/* Common Mistakes */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-[#8B4513] mb-6 flex items-center">
              <div className="w-10 h-10 flex items-center justify-center mr-4">
                <i className="ri-alert-line text-3xl text-[#B8860B]"></i>
              </div>
              Common Mistakes
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4 mb-8">
              <p>
                Through our experience providing <strong>minpaku license support</strong> in Tokyo, we have identified recurring mistakes that delay or derail applications. Avoiding these pitfalls significantly improves approval chances and reduces time to market.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white border-2 border-red-200 rounded-lg p-6 hover:border-red-400 transition-all duration-300">
                <div className="flex items-start mb-4">
                  <div className="w-12 h-12 flex items-center justify-center mr-4">
                    <i className="ri-close-circle-line text-4xl text-red-500"></i>
                  </div>
                  <h3 className="text-lg font-bold text-[#8B4513] mt-2">Inadequate Fire Safety Compliance</h3>
                </div>
                <p className="text-gray-700">
                  Failing to install required fire extinguishers, smoke detectors, and emergency lighting before inspection. Fire safety is the most common rejection reason. All equipment must meet Japanese Industrial Standards (JIS) and be properly certified.
                </p>
              </div>

              <div className="bg-white border-2 border-red-200 rounded-lg p-6 hover:border-red-400 transition-all duration-300">
                <div className="flex items-start mb-4">
                  <div className="w-12 h-12 flex items-center justify-center mr-4">
                    <i className="ri-close-circle-line text-4xl text-red-500"></i>
                  </div>
                  <h3 className="text-lg font-bold text-[#8B4513] mt-2">Missing Building Consent</h3>
                </div>
                <p className="text-gray-700">
                  Proceeding without proper authorization from building management or owners' association in condominiums. Many buildings explicitly prohibit short-term rentals in their bylaws. Verify permissions before investing in the application.
                </p>
              </div>

              <div className="bg-white border-2 border-red-200 rounded-lg p-6 hover:border-red-400 transition-all duration-300">
                <div className="flex items-start mb-4">
                  <div className="w-12 h-12 flex items-center justify-center mr-4">
                    <i className="ri-close-circle-line text-4xl text-red-500"></i>
                  </div>
                  <h3 className="text-lg font-bold text-[#8B4513] mt-2">Incomplete Floor Plans</h3>
                </div>
                <p className="text-gray-700">
                  Submitting floor plans without proper dimensions, emergency exit routes, or facility locations. Plans must be drawn to scale by qualified professionals and include all required annotations in Japanese.
                </p>
              </div>

              <div className="bg-white border-2 border-red-200 rounded-lg p-6 hover:border-red-400 transition-all duration-300">
                <div className="flex items-start mb-4">
                  <div className="w-12 h-12 flex items-center justify-center mr-4">
                    <i className="ri-close-circle-line text-4xl text-red-500"></i>
                  </div>
                  <h3 className="text-lg font-bold text-[#8B4513] mt-2">Insufficient Management Structure</h3>
                </div>
                <p className="text-gray-700">
                  Failing to demonstrate adequate 24/7 guest support and emergency response capabilities. Authorities require documented procedures and contact systems. Professional <strong>Airbnb management Tokyo</strong> services address this requirement.
                </p>
              </div>

              <div className="bg-white border-2 border-red-200 rounded-lg p-6 hover:border-red-400 transition-all duration-300">
                <div className="flex items-start mb-4">
                  <div className="w-12 h-12 flex items-center justify-center mr-4">
                    <i className="ri-close-circle-line text-4xl text-red-500"></i>
                  </div>
                  <h3 className="text-lg font-bold text-[#8B4513] mt-2">Zoning Violations</h3>
                </div>
                <p className="text-gray-700">
                  Attempting to operate in areas where short-term rentals are restricted or prohibited. Tokyo wards have varying zoning regulations. Ota City offers more favorable conditions than many other areas.
                </p>
              </div>

              <div className="bg-white border-2 border-red-200 rounded-lg p-6 hover:border-red-400 transition-all duration-300">
                <div className="flex items-start mb-4">
                  <div className="w-12 h-12 flex items-center justify-center mr-4">
                    <i className="ri-close-circle-line text-4xl text-red-500"></i>
                  </div>
                  <h3 className="text-lg font-bold text-[#8B4513] mt-2">Outdated Documentation</h3>
                </div>
                <p className="text-gray-700">
                  Submitting building registry or corporate documents older than 3 months. All official documents must be current. This seemingly minor detail causes unnecessary delays and resubmissions.
                </p>
              </div>
            </div>
          </section>

          {/* Practical Tips */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-[#8B4513] mb-6 flex items-center">
              <div className="w-10 h-10 flex items-center justify-center mr-4">
                <i className="ri-lightbulb-line text-3xl text-[#B8860B]"></i>
              </div>
              Practical Tips
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4 mb-8">
              <p>
                These practical insights come directly from our operational experience in <strong>Ota City short-term rental support</strong>. Following these recommendations significantly improves application success rates and reduces time to revenue generation.
              </p>
            </div>

            <div className="space-y-6">
              <div className="bg-gradient-to-r from-[#F5F1E8] to-white border-l-4 border-[#B8860B] p-6 rounded-r-lg">
                <h3 className="text-xl font-bold text-[#8B4513] mb-3 flex items-center">
                  <i className="ri-checkbox-circle-fill text-[#B8860B] mr-3"></i>
                  Start with Pre-Application Consultation
                </h3>
                <p className="text-gray-700 ml-9">
                  Schedule an informal consultation with your ward office before formal application. Officials can identify potential issues early and provide specific guidance for your property. This single step prevents most application rejections.
                </p>
              </div>

              <div className="bg-gradient-to-r from-[#F5F1E8] to-white border-l-4 border-[#B8860B] p-6 rounded-r-lg">
                <h3 className="text-xl font-bold text-[#8B4513] mb-3 flex items-center">
                  <i className="ri-checkbox-circle-fill text-[#B8860B] mr-3"></i>
                  Invest in Professional Fire Safety Inspection
                </h3>
                <p className="text-gray-700 ml-9">
                  Hire a qualified fire safety inspector before purchasing equipment. They ensure you install exactly what is required—no more, no less. This prevents costly mistakes and ensures first-time inspection approval.
                </p>
              </div>

              <div className="bg-gradient-to-r from-[#F5F1E8] to-white border-l-4 border-[#B8860B] p-6 rounded-r-lg">
                <h3 className="text-xl font-bold text-[#8B4513] mb-3 flex items-center">
                  <i className="ri-checkbox-circle-fill text-[#B8860B] mr-3"></i>
                  Consider Ryokan License Over Minpaku
                </h3>
                <p className="text-gray-700 ml-9">
                  If you are serious about <strong>Tokyo minpaku support</strong> as a business, the Ryokan license's year-round operation capability justifies the additional effort. The 180-day Minpaku restriction severely limits revenue potential and makes professional management uneconomical.
                </p>
              </div>

              <div className="bg-gradient-to-r from-[#F5F1E8] to-white border-l-4 border-[#B8860B] p-6 rounded-r-lg">
                <h3 className="text-xl font-bold text-[#8B4513] mb-3 flex items-center">
                  <i className="ri-checkbox-circle-fill text-[#B8860B] mr-3"></i>
                  Document Everything in Japanese
                </h3>
                <p className="text-gray-700 ml-9">
                  All official documents, floor plans, and operational procedures must be in Japanese. English translations are not accepted. Budget for professional translation services if you are not fluent in Japanese.
                </p>
              </div>

              <div className="bg-gradient-to-r from-[#F5F1E8] to-white border-l-4 border-[#B8860B] p-6 rounded-r-lg">
                <h3 className="text-xl font-bold text-[#8B4513] mb-3 flex items-center">
                  <i className="ri-checkbox-circle-fill text-[#B8860B] mr-3"></i>
                  Establish Management Systems Early
                </h3>
                <p className="text-gray-700 ml-9">
                  Set up your cleaning, maintenance, and guest communication systems before application. Authorities want to see operational readiness, not theoretical plans. Partner with established <strong>Airbnb management Tokyo</strong> providers if you lack operational experience.
                </p>
              </div>

              <div className="bg-gradient-to-r from-[#F5F1E8] to-white border-l-4 border-[#B8860B] p-6 rounded-r-lg">
                <h3 className="text-xl font-bold text-[#8B4513] mb-3 flex items-center">
                  <i className="ri-checkbox-circle-fill text-[#B8860B] mr-3"></i>
                  Budget for Unexpected Costs
                </h3>
                <p className="text-gray-700 ml-9">
                  Application fees, safety equipment, property modifications, and professional services add up quickly. Budget 500,000-1,000,000 yen beyond property acquisition costs. Undercapitalization is a common cause of abandoned applications.
                </p>
              </div>

              <div className="bg-gradient-to-r from-[#F5F1E8] to-white border-l-4 border-[#B8860B] p-6 rounded-r-lg">
                <h3 className="text-xl font-bold text-[#8B4513] mb-3 flex items-center">
                  <i className="ri-checkbox-circle-fill text-[#B8860B] mr-3"></i>
                  Maintain Detailed Records
                </h3>
                <p className="text-gray-700 ml-9">
                  Keep copies of all submitted documents, correspondence with authorities, and inspection reports. These records are invaluable for renewals, additional properties, and resolving any future compliance questions.
                </p>
              </div>

              <div className="bg-gradient-to-r from-[#F5F1E8] to-white border-l-4 border-[#B8860B] p-6 rounded-r-lg">
                <h3 className="text-xl font-bold text-[#8B4513] mb-3 flex items-center">
                  <i className="ri-checkbox-circle-fill text-[#B8860B] mr-3"></i>
                  Work with Experienced Professionals
                </h3>
                <p className="text-gray-700 ml-9">
                  The complexity of <strong>ryokan license application Tokyo</strong> processes makes professional guidance a worthwhile investment. Experienced consultants navigate bureaucracy efficiently, prevent costly mistakes, and significantly reduce time to approval. Our team's firsthand operational experience in Ota City provides practical insights that purely administrative consultants cannot offer.
                </p>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="bg-gradient-to-br from-[#8B4513] to-[#B8860B] rounded-2xl p-12 text-center text-white">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Ready to Start Your Tokyo Minpaku Business?
            </h2>
            <p className="text-xl mb-8 max-w-2xl mx-auto opacity-90">
              Get expert guidance from operators who have successfully navigated the licensing process and run profitable short-term rentals in Ota City.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/tokyo-airbnb-support#contact"
                className="inline-block bg-white text-[#8B4513] px-8 py-4 rounded-full font-semibold text-lg hover:bg-[#F5F1E8] transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap"
              >
                Schedule Free Consultation
              </Link>
              <Link
                to="/airbnb-management-tokyo"
                className="inline-block bg-transparent border-2 border-white text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-white/10 transition-all duration-300 whitespace-nowrap"
              >
                View Our Services
              </Link>
            </div>
          </section>

        </div>
      </article>

      <Footer />
    </div>
  );
}